import type { Camelized } from '@cobliteam/utils/camelize';
import { infiniteQueryOptions, type QueryClient, queryOptions, skipToken } from '@tanstack/react-query';
import {
  EventsSeveritiesSummaryDocument,
  type EventsSeveritiesSummaryQuery,
  type EventsSeveritiesSummaryQueryVariables,
  GetDeviceMediaCountDocument,
  type GetDeviceMediaCountQueryVariables,
  GetMediaDocument,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  GetMediaThumbnailDocument,
  GetMediaViewDocument,
  type GetMediaViewQuery,
} from '../__generated__/graphql';
import { requestGraphQL } from '../client/graphql';

export type { EventTrigger, MediaRequestStatus, MediaStatus, MediaType, RequestOrigin, Severity } from '../__generated__/graphql';

/** Página de Mídias como o janus devolve, em camelCase. */
export type MediaPage = NonNullable<Camelized<GetMediaQuery>['media']>;

/** Uma Mídia da lista: só os campos que o `getMedia` seleciona. */
export type MediaListItem = NonNullable<NonNullable<MediaPage['content']>[number]>;

export type MediaView = NonNullable<Camelized<GetMediaViewQuery>['mediaView']>;

export type EventsSeveritiesSummary = NonNullable<EventsSeveritiesSummaryQuery['eventsSeveritiesSummary']>;

export type MediaListVariables = GetMediaQueryVariables;
export type MediaCountVariables = GetDeviceMediaCountQueryVariables;
export type EventsSeveritiesSummaryVariables = EventsSeveritiesSummaryQueryVariables;

/** O fio do janus, em snake_case: o formato que os mocks do e2e e da demo precisam seguir. */
export type MediaPageWire = NonNullable<GetMediaQuery['media']>;
export type MediaViewWire = NonNullable<GetMediaViewQuery['mediaView']>;

/** A página da navegação entre Mídias do detalhe: 5 antes e 5 depois da Mídia aberta, por página. */
export type BidirectionalPageParam = {
  page: number;
  totalElements: number;
  afterPages: number;
  afterCount: number;
  beforePages: number;
  beforeCount: number;
};

export type BidirectionalPage = { content: MediaListItem[] } & BidirectionalPageParam;

export type BidirectionalInput = {
  kind: 'events' | 'requests';
  /** Só entra na key: a Mídia aberta muda na navegação, e a lista não pode voltar ao zero com ela. */
  filters: MediaListVariables;
  /** A Mídia aberta, que abre a primeira página entre as de antes e as de depois. */
  anchor: MediaListItem;
  /** As variables de cada lado, com `page` na posição do api-lib: os mocks do e2e casam pela ordem das chaves. */
  before: MediaListVariables;
  after: MediaListVariables;
};

// As keys ainda são as do hook legado: as mutations de `modules/hooks` e o `useCameraSafety` escrevem nelas até a Etapa 17.
const ROOT = 'CAMERA-MEDIA';
const LIST_EVENT = 'LIST-EVENT';
const LIST_REQUEST = 'LIST-REQUEST';
const DETAILS_LIST = 'DETAILS-LIST';
const BIDIRECTION_EVENTS = 'BIDIRECTION-EVENTS';
const BIDIRECTION_REQUESTS = 'BIDIRECTION-REQUESTS';
const LIST_STALE_TIME = 5 * 60 * 1000;
const BIDIRECTIONAL_PAGE_SIZE = 5;

const fetchPage = async (variables: MediaListVariables, signal: AbortSignal): Promise<MediaPage | null> => {
  const { media } = await requestGraphQL(GetMediaDocument, variables, { signal });
  return media ?? null;
};

// Como no api-lib: a página 0 não substitui a `page` das variables.
const withPage = (variables: MediaListVariables, pageParam: number): MediaListVariables => ({ ...variables, page: pageParam || variables.page });

const nextPage = (lastPage: MediaPage | null, pages: unknown[]) => (lastPage?.last === false ? pages.length : undefined);

type PagesData = { pages: ({ content?: (MediaListItem | null)[] | null } | null)[] };

const updateMediaInPages = (client: QueryClient, queryKey: readonly unknown[], mediaId: string, update: (media: MediaListItem) => MediaListItem) => {
  client.setQueriesData<PagesData>({ queryKey }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => (page ? { ...page, content: page.content?.map((media) => (media?.id === mediaId ? update(media) : media)) } : page)),
        }
      : old
  );
};

const markAsWatched = (media: MediaListItem): MediaListItem => (media.mediaStatus === 'NEW' ? { ...media, mediaStatus: 'WATCHED' } : media);

// O `mediaView` conta a visualização no pooler; as listas em cache refletem isso sem buscar de novo.
const reflectView = (client: QueryClient, mediaId: string, view: MediaView) => {
  const isEvent = view.mediaInfo?.mediaType === 'EVENT_VIDEO';
  updateMediaInPages(client, [ROOT, isEvent ? LIST_EVENT : LIST_REQUEST], mediaId, (media) => ({ ...media, mediaViewCount: (media.mediaViewCount ?? 0) + 1 }));
  updateMediaInPages(client, [ROOT, LIST_EVENT, DETAILS_LIST], mediaId, markAsWatched);
  updateMediaInPages(client, [ROOT, isEvent ? BIDIRECTION_EVENTS : BIDIRECTION_REQUESTS], mediaId, markAsWatched);
};

const initialBidirectionalParam: BidirectionalPageParam = { page: 0, totalElements: 0, afterCount: 0, afterPages: 0, beforeCount: 0, beforePages: 0 };

const sameSides = (page: BidirectionalPage): Omit<BidirectionalPageParam, 'page'> => ({
  afterCount: page.afterCount,
  afterPages: page.afterPages,
  beforeCount: page.beforeCount,
  beforePages: page.beforePages,
  totalElements: page.totalElements,
});

const fetchBidirectionalPage = async ({ anchor, before, after }: BidirectionalInput, pageParam: BidirectionalPageParam, signal: AbortSignal): Promise<BidirectionalPage> => {
  const { page } = pageParam;
  // A página positiva desce no tempo, a negativa sobe, e a 0 busca os dois lados.
  const beforePage = page >= 0 ? await fetchPage({ ...before, page, size: BIDIRECTIONAL_PAGE_SIZE }, signal) : null;
  const afterPage = page <= 0 ? await fetchPage({ ...after, page: page === 0 ? 0 : -page, size: BIDIRECTIONAL_PAGE_SIZE }, signal) : null;

  const beforeContent = (beforePage?.content ?? []).filter((media): media is MediaListItem => !!media);
  const afterContent = (afterPage?.content ?? []).filter((media): media is MediaListItem => !!media);
  const content = afterContent.toReversed().concat(page === 0 ? [anchor, ...beforeContent] : beforeContent);

  if (page !== 0) return { content, ...pageParam };
  const afterCount = afterPage?.totalElements ?? 0;
  const beforeCount = beforePage?.totalElements ?? 0;
  return {
    content,
    page,
    afterCount,
    afterPages: afterPage?.totalPages ?? 0,
    beforeCount,
    beforePages: beforePage?.totalPages ?? 0,
    totalElements: afterCount + beforeCount + 1,
  };
};

export const mediaQueries = {
  all: () => [ROOT] as const,

  /** Prefixo de todos os `view`, para invalidar a Mídia aberta em qualquer tela. */
  views: () => [...mediaQueries.all(), 'VIEW'] as const,

  /**
   * A Mídia com a URL assinada do vídeo. Buscar conta uma visualização, e as listas em cache passam a mostrar a
   * contagem nova e a Mídia como vista. Sem `mediaId`, a query fica desligada.
   */
  view: (mediaId?: string, shouldGenerateDescription = false) =>
    queryOptions({
      queryKey: [...mediaQueries.views(), mediaId, shouldGenerateDescription] as const,
      queryFn: mediaId
        ? async ({ signal, client }) => {
            const { mediaView } = await requestGraphQL(GetMediaViewDocument, { mediaId, shouldGenerateDescription }, { signal });
            if (mediaView) reflectView(client, mediaId, mediaView);
            return mediaView ?? null;
          }
        : skipToken,
    }),

  /** Só a thumbnail, sem os efeitos do `view`. Não muda, então fica em cache por 5 minutos. */
  thumbnail: (mediaId?: string) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'THUMBNAIL', mediaId] as const,
      queryFn: mediaId
        ? async ({ signal }) => {
            const { mediaView } = await requestGraphQL(GetMediaThumbnailDocument, { mediaId }, { signal });
            const details = mediaView?.mediaInfo?.mediaDetails;
            if (!details) return null;
            return ('eventThumbnailUrl' in details ? details.eventThumbnailUrl : details.footageThumbnailUrl) ?? null;
          }
        : skipToken,
      staleTime: LIST_STALE_TIME,
    }),

  /** Quantas Mídias batem com as variables, sem trazer a lista. */
  count: (variables: MediaCountVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'COUNT', variables] as const,
      queryFn: async ({ signal }) => {
        const { media } = await requestGraphQL(GetDeviceMediaCountDocument, variables, { signal });
        return media?.totalElements ?? 0;
      },
      refetchOnMount: false,
    }),

  /** Uma página, de eventos ou de solicitações, pelo que as variables pedem. */
  list: (variables: MediaListVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'LIST', variables] as const,
      queryFn: ({ signal }) => fetchPage(variables, signal),
      staleTime: LIST_STALE_TIME,
    }),

  /** A tabela de eventos, página a página. */
  eventsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), LIST_EVENT, variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A tabela de solicitações, página a página. */
  requestsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), LIST_REQUEST, variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A lista de eventos ao lado do detalhe, quando ele abre com os filtros da tabela. */
  detailsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), LIST_EVENT, DETAILS_LIST, variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A lista ao lado do detalhe aberto por link: as Mídias antes e depois dele no tempo. Sem `input`, fica desligada. */
  bidirectional: (kind: BidirectionalInput['kind'], input?: BidirectionalInput) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), kind === 'events' ? BIDIRECTION_EVENTS : BIDIRECTION_REQUESTS, input?.filters] as const,
      queryFn: input ? ({ pageParam, signal }) => fetchBidirectionalPage(input, pageParam, signal) : skipToken,
      initialPageParam: initialBidirectionalParam,
      getNextPageParam: (lastPage) => (lastPage.page + 1 >= lastPage.beforePages ? undefined : { ...sameSides(lastPage), page: lastPage.page + 1 }),
      getPreviousPageParam: (firstPage) => (firstPage.page - 1 <= -firstPage.afterPages ? undefined : { ...sameSides(firstPage), page: firstPage.page - 1 }),
    }),

  /** Quantos eventos há em cada severidade, com os filtros da tabela. */
  severitiesSummary: (variables: EventsSeveritiesSummaryVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'EVENTS-SEVERITY-SUMMARY', variables] as const,
      queryFn: async ({ signal }) => {
        const { eventsSeveritiesSummary } = await requestGraphQL(EventsSeveritiesSummaryDocument, variables, { signal });
        return eventsSeveritiesSummary ?? null;
      },
    }),
};
